import React from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Rating,
  Pagination,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material'
import {
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { HistorySession, HistoryPagination } from '@store/api/historyApi'
import { getEffectiveSessionRating } from '@utils/sessionDisplay'

interface HistoryListProps {
  sessions: HistorySession[]
  pagination: HistoryPagination
  isLoading: boolean
  error: unknown
  page: number
  onPageChange: (page: number) => void
  onEmptyStateCta?: () => void
}

const HistoryList: React.FC<HistoryListProps> = ({
  sessions,
  pagination,
  isLoading,
  error,
  page,
  onPageChange,
  onEmptyStateCta,
}) => {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Failed to load history. Please try again.</Alert>
  }

  if (sessions.length === 0) {
    return (
      <Box textAlign="center" py={6}>
        <Typography variant="h6" color="text.secondary">
          No history entries found
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={1} mb={2}>
          Start logging smoking sessions to build your history.
        </Typography>
        {onEmptyStateCta && (
          <Button variant="contained" onClick={onEmptyStateCta}>
            Log a session
          </Button>
        )}
      </Box>
    )
  }

  return (
    <Box>
      <Stack spacing={2}>
        {sessions.map((session) => {
          const effectiveRating = getEffectiveSessionRating(session)
          return (
          <Card key={session.id} variant="outlined">
            <CardContent>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {session.cigar?.cigarLine?.name ?? 'Unknown Cigar'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {session.cigar?.cigarLine?.brand?.name ?? ''}{session.cigar?.vitola ? ` · ${session.cigar.vitola.name}` : ''}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box display="flex" flexWrap="wrap" gap={1} justifyContent={{ sm: 'flex-end' }}>
                    <Chip
                      icon={<CalendarIcon fontSize="small" />}
                      label={format(new Date(session.smokedDate), 'MMM d, yyyy')}
                      size="small"
                      variant="outlined"
                    />
                    {session.location && (
                      <Chip
                        icon={<LocationIcon fontSize="small" />}
                        label={session.location}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Grid>

                {effectiveRating != null && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Rating value={effectiveRating} precision={0.5} readOnly size="small" max={5} />
                      <Typography variant="body2" color="text.secondary">
                        {effectiveRating}/5
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {session.tastingNotes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      &ldquo;{session.tastingNotes}&rdquo;
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
          )
        })}
      </Stack>

      {pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={pagination.totalPages}
            page={page}
            onChange={(_e, value) => onPageChange(value)}
            color="primary"
          />
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
        Showing {sessions.length} of {pagination.totalCount} entries
      </Typography>
    </Box>
  )
}

export default HistoryList
